import { PageHeader } from "@/components/layout/PageHeader";
import { Loading } from "@/components/shared/Loading";
import { EmptyState } from "@/components/shared/EmptyState";
import { useAudit } from "@/api/hooks";
import { ScrollText, Sparkles } from "lucide-react";

const ACTION_LABEL = {
  "credit.decision": "Credit decision", "supplier_invoice.approve": "Supplier invoice approved",
  "supplier_invoice.reject": "Supplier invoice rejected", "supplier_invoice.escalate": "Variance escalated",
  "approval.decision": "Approval decision", "exception.resolved": "Exception resolved",
  "invoice.prepared": "Invoice prepared", "document.approved": "Document approved",
  "document.uploaded": "Document uploaded", "exception.created": "Document exception raised",
  "message.logged": "Message logged", "message.received": "Inbound message received", "ai.summarize": "AI summary generated",
};

export default function Audit() {
  const { data: log, error } = useAudit();
  if (error) return <div className="p-6 text-slate-500">Audit log is restricted for your role.</div>;
  if (!log) return <Loading />;

  return (
    <>
      <PageHeader title="Audit Log" subtitle="Every action is attributed, timestamped and traceable" />
      <div className="flex-1 overflow-y-auto p-6">
        {log.length === 0 ? <EmptyState icon={ScrollText} title="No actions recorded yet" sub="Actions across the workspace will appear here." /> : (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400"><th className="text-left font-semibold px-4 py-2.5">When</th><th className="text-left font-semibold px-4 py-2.5">Actor</th><th className="text-left font-semibold px-4 py-2.5">Action</th><th className="text-left font-semibold px-4 py-2.5">Entity</th><th className="text-left font-semibold px-4 py-2.5">Note</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {log.map((e) => {
                  const isAI = e.action.startsWith("ai.");
                  return (
                    <tr key={e.id} data-testid={`audit-${e.id}`} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-2.5 text-[11px] font-mono-num text-slate-400 whitespace-nowrap">{new Date(e.ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="px-4 py-2.5"><span className="text-slate-700">{e.actorName}</span><span className="ml-1 text-[10px] uppercase tracking-wider text-slate-400 capitalize">{e.actorRole}</span></td>
                      <td className="px-4 py-2.5"><span className={`inline-flex items-center gap-1 font-medium ${isAI ? "text-violet-700" : "text-slate-700"}`}>{isAI && <Sparkles className="h-3 w-3" />}{ACTION_LABEL[e.action] || e.action}</span></td>
                      <td className="px-4 py-2.5 font-mono-num text-[12px] text-slate-500">{e.entity}/{e.entityId}</td>
                      <td className="px-4 py-2.5 text-[12px] text-slate-500">{e.note}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
